// src/components/sops/SopsLibrary.js — sopweb-bdb7afcd (SOPs Library, Seam 2)
//
// The library: folder tree on the left, the SOP table on the right. Filters All / Must-read /
// Archived, a title search (on the loaded list — no PDF text search), New folder, + New SOP, folder
// rename / move / archive / restore, Open (new tab through openSop).
// READS: once on open (and after a change): every folder + every SOP of the company — one read each.
// The "who can see" / must-read numbers are stored on each SOP by the server (no per-SOP lookups).
// sopread-7e4c2a93 — "Read status" = "X of Y read" from readCount / mustReadCount on each SOP (also
// server-kept; back to 0 on a new version). The "Manage library" tab of the SOPs page (tabBar).
// sopremind-9c4e7a21 (Seam 4) — the red overdue banner + "Overdue" filter, from overdueCount on each SOP
// (server-kept by the 7 a.m. run, confirms and access changes — no extra reads). /sops/library?filter=
// overdue (the managers' summary email) opens on that filter.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, BookOpen, FolderClosed, FolderOpen, MoreHorizontal, Plus, Search, ExternalLink, Download } from 'lucide-react';
import { Button, Notice, OPENED_NOTE, downloadedNote, inputClass } from './SopUi';
import SopFolderDialog from './SopFolderDialog';
import { loadFolders, loadSops, openSopInNewTab, downloadSop, friendlyError } from './sopsApi';
import {
    LIBRARY_FILTERS, filterSops, folderAndBelow, folderCounts, folderPathText, foldersById, liveFolderList,
    readStatusText, canSeeText, dateText,
    LIBRARY_FILTER_PARAM, libraryFilterFrom, overdueSummary, overdueBannerText, // sopremind-9c4e7a21
} from '../../utils/sopsUi';

function FolderMenu({ onPick }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button type="button" aria-label="Folder actions" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className="rounded p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100"><MoreHorizontal size={16} /></button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
                    <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border bg-white py-1 shadow-lg text-sm">
                        {[['rename', 'Rename'], ['move', 'Move'], ['archive', 'Archive']].map(([k, l]) => (
                            <button key={k} type="button" onClick={(e) => { e.stopPropagation(); setOpen(false); onPick(k); }}
                                className={`block w-full px-3 py-1.5 text-left hover:bg-gray-50 ${k === 'archive' ? 'text-red-700' : 'text-gray-800'}`}>{l}</button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default function SopsLibrary({ orgId, tabBar }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [folders, setFolders] = useState([]);
    const [sops, setSops] = useState([]);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [filter, setFilter] = useState(() => libraryFilterFrom(new URLSearchParams((location && location.search) || '').get(LIBRARY_FILTER_PARAM)));
    const [folderId, setFolderId] = useState(null);
    const [text, setText] = useState('');
    const [dialog, setDialog] = useState(null); // { mode, folder }
    const [notice, setNotice] = useState(null);

    const reload = useCallback(async () => {
        if (!orgId) return;
        try {
            const [f, s] = await Promise.all([loadFolders(orgId), loadSops(orgId)]);
            setFolders(f); setSops(s); setStatus('done'); setError('');
        } catch (e) {
            console.warn('sopweb: library load failed', e);
            setStatus('error'); setError(friendlyError(e, 'The SOPs couldn’t be loaded. Please try again.'));
        }
    }, [orgId]);
    useEffect(() => { reload(); }, [reload]);

    const byId = useMemo(() => foldersById(folders), [folders]);
    const tree = useMemo(() => liveFolderList(folders), [folders]);
    const archivedFolders = useMemo(() => folders.filter((f) => f.archived === true).sort((a, b) => String(a.name).localeCompare(String(b.name))), [folders]);
    // A selected folder that became archived (or vanished) falls back to All SOPs.
    const selected = folderId && byId.has(folderId) && (byId.get(folderId).archived !== true || filter === 'archived') ? folderId : null;
    const underSelected = useMemo(() => (selected ? folderAndBelow(folders, selected) : null), [folders, selected]);
    const inFilter = useMemo(() => filterSops(sops, { filter, text }), [sops, filter, text]);
    const counts = useMemo(() => folderCounts(folders, inFilter), [folders, inFilter]);
    const rows = useMemo(() => filterSops(sops, { filter, text, folderIds: underSelected }), [sops, filter, text, underSelected]);
    const overdue = useMemo(() => overdueSummary(sops), [sops]); // sopremind-9c4e7a21

    const open = async (sop) => {
        setNotice(null);
        try { await openSopInNewTab(sop.id); setNotice({ tone: 'info', text: OPENED_NOTE }); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be opened. Please try again.') }); }
    };
    // sopweb-bdb7afcd — Download: the current version, saved as "<title> – v<N>.pdf".
    const download = async (sop) => {
        setNotice(null);
        try { const res = await downloadSop(sop.id); setNotice({ tone: 'info', text: downloadedNote(res.fileName) }); }
        catch (e) { setNotice({ tone: 'error', text: friendlyError(e, 'The SOP couldn’t be downloaded. Please try again.') }); }
    };

    const folderRow = (f) => {
        const active = selected === f.id;
        return (
            <div key={f.id} onClick={() => setFolderId(f.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setFolderId(f.id); }}
                className={`group flex items-center gap-1.5 rounded-lg py-1.5 pr-1 text-sm cursor-pointer ${active ? 'bg-[#5F4EC9]/10 text-[#5F4EC9] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                style={{ paddingLeft: `${8 + (f.depth || 0) * 16}px` }}>
                {active ? <FolderOpen size={16} className="flex-shrink-0" /> : <FolderClosed size={16} className="flex-shrink-0 text-gray-400" />}
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-xs text-gray-400 tabular-nums">{counts.get(f.id) || 0}</span>
                {f.archived ? (
                    <button type="button" onClick={(e) => { e.stopPropagation(); setDialog({ mode: 'restore', folder: f }); }} className="text-xs text-[#5F4EC9] hover:underline px-1">Restore</button>
                ) : (
                    <FolderMenu onPick={(mode) => setDialog({ mode, folder: f })} />
                )}
            </div>
        );
    };

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-2 mr-auto">
                    <BookOpen size={24} className="text-[#5F4EC9]" />
                    <h1 className="text-2xl font-bold text-[#12192D]">SOPs</h1>
                </div>
                <Button onClick={() => setDialog({ mode: 'new', folder: null })}><Plus size={16} /> New folder</Button>
                <Button variant="primary" onClick={() => navigate('/sops/new', { state: { folderId: selected } })}><Plus size={16} /> New SOP</Button>
            </div>
            {tabBar && <div className="mb-4">{tabBar}</div>}

            {notice && <div className="mb-4"><Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice></div>}
            {status === 'error' && <div className="mb-4"><Notice tone="error">{error} <button type="button" className="underline ml-1" onClick={reload}>Try again</button></Notice></div>}
            {status === 'done' && overdue.reads > 0 && (
                <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <AlertTriangle size={18} className="flex-shrink-0" aria-hidden="true" />
                    <span className="flex-1 min-w-0 font-medium">{overdueBannerText(overdue)}</span>
                    {filter !== 'overdue' && (
                        <button type="button" onClick={() => { setFilter('overdue'); setFolderId(null); }}
                            className="rounded-lg border border-red-300 bg-white px-3 py-1.5 font-medium text-red-800 hover:bg-red-100">Show overdue</button>
                    )}
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-4">
                {/* Folder tree */}
                <aside className="md:w-64 flex-shrink-0 rounded-xl border bg-white p-2 self-start w-full">
                    <div onClick={() => setFolderId(null)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setFolderId(null); }}
                        className={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm cursor-pointer ${!selected ? 'bg-[#5F4EC9]/10 text-[#5F4EC9] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                        <BookOpen size={16} className="flex-shrink-0" />
                        <span className="flex-1">{filter === 'archived' ? 'All archived SOPs' : 'All SOPs'}</span>
                        <span className="text-xs text-gray-400 tabular-nums">{inFilter.length}</span>
                    </div>
                    {tree.map(folderRow)}
                    {status === 'done' && tree.length === 0 && <p className="px-2 py-2 text-xs text-gray-500">No folders yet. Click <strong>New folder</strong> to make one.</p>}
                    {filter === 'archived' && archivedFolders.length > 0 && (
                        <div className="mt-3 border-t pt-2">
                            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Archived folders</p>
                            {archivedFolders.map((f) => folderRow({ ...f, depth: 0 }))}
                        </div>
                    )}
                </aside>

                {/* SOP table */}
                <section className="flex-1 min-w-0 rounded-xl border bg-white">
                    <div className="flex flex-wrap items-center gap-2 border-b p-3">
                        <div className="flex rounded-lg border p-0.5" role="tablist" aria-label="Which SOPs">
                            {LIBRARY_FILTERS.map((f) => (
                                <button key={f.value} type="button" role="tab" aria-selected={filter === f.value} onClick={() => setFilter(f.value)}
                                    className={`rounded-md px-3 py-1 text-sm ${filter === f.value ? 'bg-[#12192D] text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{f.label}</button>
                            ))}
                        </div>
                        <div className="relative ml-auto w-full sm:w-64">
                            <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input aria-label="Search SOP titles" className={`${inputClass} pl-8`} placeholder="Search titles" value={text} onChange={(e) => setText(e.target.value)} />
                        </div>
                    </div>
                    {selected && <p className="px-3 pt-2 text-xs text-gray-500">In {folderPathText(byId, selected)} (and its sub-folders)</p>}

                    {status === 'loading' ? (
                        <p className="p-8 text-center text-gray-500 animate-pulse">Loading SOPs…</p>
                    ) : rows.length === 0 ? (
                        <p className="p-8 text-center text-sm text-gray-500">
                            {text ? 'No SOP titles match that search.' : filter === 'archived' ? 'No archived SOPs here.' : filter === 'must' ? 'No must-read SOPs here.' : filter === 'overdue' ? 'Nobody is overdue on an SOP here.' : 'No SOPs here yet. Click + New SOP to add one.'}
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                                    <tr className="border-b">
                                        <th className="px-3 py-2 font-medium">Title</th>
                                        <th className="px-3 py-2 font-medium">Folder</th>
                                        <th className="px-3 py-2 font-medium">Version</th>
                                        <th className="px-3 py-2 font-medium">Last updated</th>
                                        <th className="px-3 py-2 font-medium" title="People given access. Owners, Admins and Managers can always see every SOP and are counted only when they must read it.">Who can see</th>
                                        <th className="px-3 py-2 font-medium" title="People who must read it and have read the current version">Read status</th>
                                        <th className="px-3 py-2 font-medium">Catalog items</th>
                                        <th className="px-3 py-2" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((s) => (
                                        <tr key={s.id} onClick={() => navigate(`/sops/${s.id}`)} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer">
                                            <td className="px-3 py-2.5 font-medium text-[#12192D]">{s.title}</td>
                                            <td className="px-3 py-2.5 text-gray-600">{folderPathText(byId, s.folderId) || '—'}</td>
                                            <td className="px-3 py-2.5 text-gray-600 tabular-nums">v{s.currentVersionNumber || 1}</td>
                                            <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{dateText(s.updatedAt)}</td>
                                            <td className="px-3 py-2.5 text-gray-600 tabular-nums">{canSeeText(s)}</td>
                                            <td className="px-3 py-2.5">
                                                <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs ${s.mustReadCount > 0 ? 'bg-[#5F4EC9]/10 text-[#5F4EC9]' : 'bg-gray-100 text-gray-600'}`}>{readStatusText(s)}</span>
                                            </td>
                                            <td className="px-3 py-2.5 text-gray-600 tabular-nums">{(s.catalogServiceIds || []).length}</td>
                                            <td className="px-3 py-2.5 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button onClick={(e) => { e.stopPropagation(); open(s); }} className="!min-h-[32px] !py-1"><ExternalLink size={14} /> Open</Button>
                                                    <Button onClick={(e) => { e.stopPropagation(); download(s); }} className="!min-h-[32px] !py-1" aria-label={`Download ${s.title}`}><Download size={14} /> Download</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <p className="border-t px-3 py-2 text-xs text-gray-500">Owners, Admins and Managers can always see every SOP. “Who can see” counts the people given access.</p>
                </section>
            </div>

            {dialog && (
                <SopFolderDialog mode={dialog.mode} folder={dialog.folder} folders={folders} defaultParentId={selected}
                    onClose={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />
            )}
        </div>
    );
}
